import { toast } from 'react-toastify';
import { SpaceErrorCode, WorkspaceListItem } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { WORKSPACE_INVALIDATION_KEYS } from '../constants/spaces-query.const';
import { CreateWorkspacePayload } from '../validations/create-workspace.validation';

type PatchWorkspaceVariables = { id: string } & Partial<CreateWorkspacePayload>;

type Props = {
  onDuplicateName?: () => void;
  onSuccess?: (workspace: WorkspaceListItem) => void;
};

export const usePatchWorkspace = ({ onDuplicateName, onSuccess }: Props = {}) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const { messagesLocale } = useLocale();
  const { invalidateQuery } = useInvalidateQueries(...WORKSPACE_INVALIDATION_KEYS);

  const { isPending, mutate: patchWorkspace } = useAppMutation<
    WorkspaceListItem,
    HttpException,
    PatchWorkspaceVariables
  >([QueryKey.PATCH_WORKSPACE, companyId], {
    // Replaced, as on create: a taken name goes to the field, anything else gets a toast.
    defaultErrorHandling: false,
    mutationFn: ({ id, ...body }) =>
      ApiService.patch<WorkspaceListItem, Partial<CreateWorkspacePayload>>(
        { endpoint: BackendRoute.WORKSPACES, routePath: [id] },
        { body },
      ),
    onError: (error) => {
      if (error.body?.code === SpaceErrorCode.WORKSPACE_NAME_TAKEN) {
        onDuplicateName?.();

        return;
      }

      toast.error(messagesLocale['FAILED_TO_SAVE_CHANGES']);
    },
    onSuccess: (workspace) => {
      toast.success(messagesLocale['CHANGES_SAVED']);
      invalidateQuery();
      onSuccess?.(workspace);
    },
  });

  return { isPending, patchWorkspace };
};
